import { ImageDetailDto, ImageListItemDto, ImageUsageDto, ImageVariantDto } from '../types/admin.types';

export const IMAGE_VARIANT_LABELS: Record<number, string> = {
    0: 'Original',
    1: 'Large',
    2: 'Medium',
    3: 'Small',
    4: 'Thumbnail'
};

export const IMAGE_STATUS_LABELS: Record<number, string> = {
    0: 'Pending',
    1: 'Processing',
    2: 'Ready',
    3: 'Failed'
};

export const normalizeVariantType = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        const parsed = Number(trimmed);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }

        const lowered = trimmed.toLowerCase();
        if (lowered === 'original') return 0;
        if (lowered === 'large') return 1;
        if (lowered === 'medium') return 2;
        if (lowered === 'small') return 3;
        if (lowered === 'thumbnail') return 4;
    }

    return null;
};

export const getVariantLabel = (value: unknown): string => {
    const normalized = normalizeVariantType(value);
    if (normalized != null) {
        return IMAGE_VARIANT_LABELS[normalized] ?? `Variant ${normalized}`;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
        return value;
    }

    return 'Unknown';
};

export const getVariantOrder = (value: unknown): number => {
    const normalized = normalizeVariantType(value);
    if (normalized == null) {
        return 999;
    }
    return normalized;
};

export const getVariantUrl = (variants: ImageVariantDto[] | undefined, variantType: unknown): string | null => {
    if (!variants || variants.length === 0) {
        return null;
    }

    const normalized = normalizeVariantType(variantType);
    if (normalized != null) {
        const match = variants.find((variant) => normalizeVariantType(variant.variantType) === normalized);
        if (match?.url) {
            return match.url;
        }
    }

    return variants.find((variant) => Boolean(variant.url))?.url ?? null;
};

export const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    const formatted = value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1);
    return `${formatted} ${units[index]}`;
};

export const mapImageListItem = (item: any): ImageListItemDto => ({
    id: item.id ?? item.Id ?? '',
    title: item.title ?? item.Title ?? null,
    altText: item.altText ?? item.AltText ?? null,
    originalFileName: item.originalFileName ?? item.OriginalFileName ?? '',
    thumbnailUrl: item.thumbnailUrl ?? item.ThumbnailUrl ?? null,
    status: item.status ?? item.Status ?? 0,
    width: item.width ?? item.Width ?? 0,
    height: item.height ?? item.Height ?? 0,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? ''
});

const mapVariant = (variant: any): ImageVariantDto => ({
    variantType: variant.variantType ?? variant.VariantType ?? 0,
    url: variant.url ?? variant.Url ?? null,
    mimeType: variant.mimeType ?? variant.MimeType ?? '',
    fileSizeBytes: variant.fileSizeBytes ?? variant.FileSizeBytes ?? 0,
    width: variant.width ?? variant.Width ?? 0,
    height: variant.height ?? variant.Height ?? 0
});

const mapUsage = (usage: any): ImageUsageDto => ({
    ownerType: usage.ownerType ?? usage.OwnerType ?? 0,
    ownerId: usage.ownerId ?? usage.OwnerId ?? '',
    variantType: usage.variantType ?? usage.VariantType ?? 0,
    createdDate: usage.createdDate ?? usage.CreatedDate ?? ''
});

export const mapImageDetail = (item: any): ImageDetailDto => ({
    id: item.id ?? item.Id ?? '',
    title: item.title ?? item.Title ?? null,
    altText: item.altText ?? item.AltText ?? null,
    caption: item.caption ?? item.Caption ?? null,
    description: item.description ?? item.Description ?? null,
    attribution: item.attribution ?? item.Attribution ?? null,
    sourceUrl: item.sourceUrl ?? item.SourceUrl ?? null,
    originalFileName: item.originalFileName ?? item.OriginalFileName ?? '',
    status: item.status ?? item.Status ?? 0,
    width: item.width ?? item.Width ?? 0,
    height: item.height ?? item.Height ?? 0,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? '',
    variants: (item.variants ?? item.Variants ?? []).map(mapVariant),
    usages: (item.usages ?? item.Usages ?? []).map(mapUsage)
});
